import loadable from '@loadable/component';
import { Container } from '@mui/material';
import type { RouteComponentProps } from '@reach/router';
import React, { useMemo, useState } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import BackgroundIcon from '@components/BackgroundIcon';
import ContentLoader from '@components/ContentLoader';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import ViewTab from '@components/ViewTab';

import { useGetRoutines } from '@hooks/api/routine';

import { MainContainer, WrapperDiv } from '@layouts/Layouts/BigTitleLayout';

import type { RoutineEvent } from '@models/routine';

import { Routines } from '@views/Dashboard';

import { Body } from './Main.style';
import RoutineList from './RoutineList/RoutineList';

/**
 * The scheduler pulls in the DevExpress scheduler packages (and moment through
 * dx-scheduler-core), which is the heaviest thing on this route. Only the
 * Calendar tab needs it, and it needs a DOM, so keep it out of the page chunk
 * and out of the SSR build.
 */
const RoutineScheduler = loadable(() => import('./RoutineScheduler'), {
  ssr: false,
  fallback: <ContentLoader loading>{null}</ContentLoader>,
});

const messages = defineMessages({
  title: {
    id: 'routinesMain.title',
    defaultMessage: 'Routines',
  },
  calendar: {
    id: 'routinesMain.calendar',
    defaultMessage: 'Calendar',
  },
  todaysRoutines: {
    id: 'routinesMain.todaysRoutines',
    defaultMessage: "Today's Routines",
  },
  allRoutines: {
    id: 'routinesMain.allRoutines',
    defaultMessage: 'All Routines',
  },
});

const RoutineWrapper: React.FC<RouteComponentProps> = () => {
  const { formatMessage } = useIntl();

  const { data, loading } = useGetRoutines();

  const date = useMemo(() => new Date(), []);
  const view =
    typeof window === 'undefined'
      ? 'Week'
      : sessionStorage.getItem('calendarView');

  const tabs = [
    { id: '0', value: formatMessage(messages.todaysRoutines) },
    { id: '1', value: formatMessage(messages.calendar) },
    { id: '2', value: formatMessage(messages.allRoutines) },
  ];
  const [activeTab, setActiveTab] = useState(tabs[1]);

  const scheduledRoutines = useMemo(
    () =>
      data
        .map(({ event }) => event)
        .filter((event): event is RoutineEvent => Boolean(event)),
    [data]
  );

  const getCurrentView = (tabId: string) => {
    switch (tabId) {
      case '0':
        return (
          <Container sx={{ marginTop: '1%' }} maxWidth="md">
            <Routines orientation="grid" />
          </Container>
        );
      case '1':
        return (
          <div
            style={{ height: 'calc(100% - 65px)', width: 'calc(100% - 25px)' }}
          >
            <RoutineScheduler
              date={date}
              scheduledRoutines={scheduledRoutines}
              view={
                view === 'Day' || view === 'Week' || view === 'Month'
                  ? view
                  : 'Week'
              }
            />
          </div>
        );
      case '2':
      default:
        return (
          <Container maxWidth="sm" sx={{ my: 2 }}>
            <RoutineList routines={data} showHeader={false} />
          </Container>
        );
    }
  };

  return (
    <ContentLoader loading={loading}>
      <Body>
        <MainContainer style={{ height: '100%', paddingBottom: 0 }}>
          <BackgroundIcon />
          <WrapperDiv>
            <ViewTab
              changeTab={setActiveTab}
              selected={activeTab}
              tabArray={tabs}
            />
          </WrapperDiv>
          {getCurrentView(activeTab.id)}
        </MainContainer>
      </Body>
    </ContentLoader>
  );
};

export default withErrorBoundary(React.memo(RoutineWrapper), {
  FallbackComponent: (props) => (
    <Container
      maxWidth="lg"
      sx={{
        mt: {
          sm: 7,
          md: 0,
        },
      }}
    >
      <ErrorBoundaryFallback {...props} />
    </Container>
  ),
});
